import isEmpty from 'lodash/isEmpty'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import Filters from '../components/Filters'
import ProductGrid from '../components/ProductGrid'
import Layout, { LayoutHeaderSlot } from '../layout/Layout'
import Subheader from '../components/Subheader'
import { useDebouncedCallback } from 'use-debounce'
import useLocalStorageState from 'use-local-storage-state'
import ColumnChooserPopover from '../components/ColumnChooserPopover'
import RegionManager from '../components/RegionManager'
import NotificationBar from '../components/NotificationBar'
import { useColumnSettingsLoader } from '../hooks/useColumnSettingsSync'

// Reihenfolge spiegelt die Spalten-Definition in ProductGrid (allColumns):
// nach einem Reset (keine gespeicherte Order) zeigt der Column Chooser
// die Liste in GENAU dieser Reihenfolge an.
const COLUMNS = [
  ['amz_img', 'Amazon img'],
  ['actions', 'Befehle'],
  ['keepa_chart_dk', 'Chart'],
  ['flags', 'Flaggen'],
  ['margin', 'Marge'],
  ['roi', 'Roi'],
  ['profit', 'Profit'],
  ['salesrank', 'BSR'],
  ['percent_bsr', 'BSR%'],
  ['asin', 'Asin'],
  ['supplier_url', 'Supplier'],
  ['name', 'Title'],
  ['ean', 'EAN'],
  ['sellprice', 'Sellprice'],
  ['confirmed_sales', 'Conf. Sales'],
  ['salesdrops', 'Salesdrops'],
  ['variations', 'Variations'],
  ['buyprice', 'Buyprice'],
  ['timestamp', 'Timestamp'],
]

const application = 'a2a'

export default function AmazonFlips() {
  let dispatch = useDispatch()
  let [regions, setRegions] = useLocalStorageState('selected-regions-a2a', {
    defaultValue: ['de', 'it', 'es', 'fr']
  })
  let [search, setSearch] = useState('')
  let onSearchDebounced = useDebouncedCallback((value) => setSearch(value), 500)
  // Kontrollierter Wert nur fuer die Such-Pille (Clear-Button);
  // das eigentliche Suchen laeuft weiter debounced ueber setSearch.
  let [searchText, setSearchText] = useState('')
  const handleSearch = (value) => {
    setSearchText(value)
    onSearchDebounced(value)
  }

  let [hiddenColumnIds, setHiddenColumnIds] = useLocalStorageState(`${application}-col-hidden`, {
    defaultValue: []
  })

  let [columnOrder, setColumnOrder] = useLocalStorageState(`${application}-col-order`)
  // Gleicher Key wie in ProductGrid (use-local-storage-state synct) --
  // der Ansicht-Switch lebt jetzt im ColumnChooserPopover.
  let [viewMode, setViewMode] = useLocalStorageState(`${application}-view-mode`, {
    defaultValue: 'table'
  })
  let [columnSizing, setColumnSizing] = useLocalStorageState(`${application}-col-sizing`, {
    defaultValue: {}
  })

  useColumnSettingsLoader({
    module: application,
    setColumnOrder,
    setHiddenColumnIds,
    setColumnSizing,
  })

  let [tutorialModal, setTutorialModal] = useState(false)

  return (
    <Layout scrollWithHeader>
      <section className="overflow-hidden w-full h-full flex flex-col">
        {/* Einziger Scroller der Seite (vertikal + horizontal): die EDGE-Leiste
            (LayoutHeaderSlot) scrollt vertikal mit weg, das Seiten-Chrome bleibt
            per sticky left beim Quer-Scrollen stehen, die thead-Spaltennamen
            kleben per sticky top am oberen Rand. */}
        <div className="flex-1 min-h-0 flex flex-col overflow-auto no-h-scrollbar" style={{ overflowAnchor: 'none' }}>
          <LayoutHeaderSlot />
          <div className="sticky left-0 z-20 bg-background">
        <header className="px-4 pt-4 pb-5">
          <Subheader subtitle="Deine Filter für A2A Flips">
            A2A <em>Flips</em>
          </Subheader>

          {/* Toolbar-Bar: Controls in eigenem umrandeten Balken. Suche fuellt
              links (gekappt), Buttons rechts. */}
          <div className='mt-5 flex flex-wrap items-center gap-2.5 p-2.5 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-bgColor dark:bg-white/[0.02]'>
            {/* Such-Pille — fuellt den Platz links */}
            <div className="relative flex-1 min-w-[220px] max-w-[380px] group">
              <i className="bx bx-search absolute left-3 top-1/2 -translate-y-1/2 text-base text-neutral-400 pointer-events-none group-focus-within:text-primary"></i>
              <input
                type="text"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Produkt, ASIN oder EAN suchen"
                className="w-full h-9 pl-9 pr-9 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-background text-sm text-textTitle placeholder:text-neutral-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
              />
              {searchText && (
                <button
                  type="button"
                  onClick={() => handleSearch('')}
                  aria-label="Leeren"
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-6 w-6 rounded text-neutral-400 hover:text-textTitle"
                >
                  <i className="bx bx-x text-lg leading-none"></i>
                </button>
              )}
            </div>

            <span className='flex-1'></span>

            <RegionManager regions={regions} setRegions={setRegions} />
            <NotificationBar application={application} />

            <ColumnChooserPopover
              application='a2a'
              // Card View hat keine konfigurierbaren Spalten -> Sektion weg
              columns={viewMode === 'cards' ? null : COLUMNS}
              viewOptions={{
                value: viewMode,
                onChange: setViewMode,
                options: [
                  { value: 'table', label: 'Multiview' },
                  { value: 'cards', label: 'Card View' },
                ],
              }}
              onReset={(!isEmpty(hiddenColumnIds) || !!columnOrder || !isEmpty(columnSizing)) && (() => {
                setHiddenColumnIds([])
                setColumnOrder(null)
                setColumnSizing({})
              })}
            >{({ setIsOpen, isOpen }) =>
              <button
                type='button'
                onClick={() => setIsOpen(val => !val)}
                className={`relative inline-flex items-center gap-2 h-9 px-3 rounded-lg border text-sm font-medium transition-colors ${isOpen ? 'border-primary text-primary bg-secondary' : 'border-neutral-200 dark:border-neutral-700 text-textTitle bg-background hover:border-primary'}`}
              >
                <i className='bx bx-slider-alt text-[17px] leading-none' aria-hidden></i>
                Ansicht
                <i className={`bx bx-chevron-down text-base leading-none -ml-0.5 ${isOpen ? 'text-primary' : 'text-neutral-400'}`} aria-hidden></i>
              </button>}
            </ColumnChooserPopover>
          </div>
        </header>
        <div className='px-4 pb-2'>
          <Filters application={application} onChange={() => { }} />
        </div>
          </div>
          <ProductGrid
            application={application}
            regions={regions}
            search={search}
            hiddenColumnIds={hiddenColumnIds}
            outerScroll
          />
        </div>
      </section>

    </Layout>
  )
}
